/**
 * Character Library — the user's reusable cast (Bella, Benny, Mom, …).
 *
 * Each character has: name, description, optional role, optional tags,
 * and zero-or-more reference photos stored in GridFS so they survive
 * production redeploys.
 *
 * From here, channel kit, storyboard, and auto-video flows can opt-in
 * to pull characters so the same Bella renders consistently across
 * the avatar, banner, thumbnail, and every scene.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import {
  listCharacters, createCharacter, updateCharacter, deleteCharacter,
  uploadCharacterImage, deleteCharacterImage, characterImageUrl,
} from "@/lib/charactersApi";

function CharacterImage({ characterId, image, onDelete, disabled }) {
  return (
    <div
      data-testid={`character-image-${image.image_id}`}
      className="relative group border border-zinc-800 rounded-sm overflow-hidden bg-zinc-950"
    >
      <img
        src={characterImageUrl(characterId, image.image_id)}
        alt="character reference"
        className="w-full aspect-square object-cover"
      />
      <button
        type="button"
        disabled={disabled}
        onClick={() => onDelete(image.image_id)}
        data-testid={`character-image-delete-${image.image_id}`}
        title="Remove reference image"
        className="absolute top-1 right-1 bg-black/80 hover:bg-red-600 text-white text-xs font-bold px-2 py-0.5 rounded-sm opacity-0 group-hover:opacity-100 transition-opacity disabled:opacity-50"
      >
        ✕
      </button>
    </div>
  );
}

function CharacterCard({ character, onChange, onDelete }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState({
    name: character.name,
    description: character.description || "",
    role: character.role || "",
  });
  const [busy, setBusy] = useState(false);
  const fileInputRef = useRef(null);

  const saveEdits = async () => {
    if (!draft.name.trim()) {
      toast.error("Name is required");
      return;
    }
    setBusy(true);
    try {
      const updated = await updateCharacter(character.character_id, draft);
      onChange(updated);
      setEditing(false);
      toast.success("Saved");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't save");
    } finally {
      setBusy(false);
    }
  };

  const handleFiles = async (files) => {
    if (!files || !files.length) return;
    setBusy(true);
    let latest = character;
    for (const f of files) {
      try {
        latest = await uploadCharacterImage(character.character_id, f);
      } catch (err) {
        toast.error(err?.response?.data?.detail || `Couldn't upload ${f.name}`);
      }
    }
    onChange(latest);
    setBusy(false);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const removeImage = async (imageId) => {
    setBusy(true);
    try {
      const updated = await deleteCharacterImage(character.character_id, imageId);
      onChange(updated);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't remove image");
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    if (!window.confirm(`Delete ${character.name}? This removes the character + all its reference images.`)) return;
    setBusy(true);
    try {
      await deleteCharacter(character.character_id);
      onDelete(character.character_id);
      toast.success(`Deleted ${character.name}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't delete");
      setBusy(false);
    }
  };

  const images = character.images || [];

  return (
    <div
      data-testid={`character-card-${character.character_id}`}
      className="border border-zinc-800 hover:border-zinc-700 transition-colors rounded-sm bg-zinc-950/40 p-4 space-y-3"
    >
      <div className="flex items-start justify-between gap-2">
        <div className="flex-1 min-w-0">
          {editing ? (
            <input
              data-testid={`character-name-input-${character.character_id}`}
              value={draft.name}
              onChange={(e) => setDraft({ ...draft, name: e.target.value })}
              className="w-full bg-black border border-yellow-400/40 text-white px-2 py-1 font-bold text-lg rounded-sm"
              placeholder="Name (e.g. Bella)"
            />
          ) : (
            <h3 className="text-white font-bold text-lg truncate">{character.name}</h3>
          )}
          {editing ? (
            <input
              data-testid={`character-role-input-${character.character_id}`}
              value={draft.role}
              onChange={(e) => setDraft({ ...draft, role: e.target.value })}
              className="mt-1 w-full bg-black border border-zinc-700 text-zinc-300 px-2 py-1 font-mono text-[11px] uppercase tracking-wider rounded-sm"
              placeholder="Role (e.g. main, sidekick, parent)"
            />
          ) : character.role ? (
            <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-yellow-400 mt-0.5">
              {character.role}
            </div>
          ) : null}
        </div>
        <div className="flex gap-1 shrink-0">
          {editing ? (
            <>
              <button
                type="button"
                onClick={saveEdits}
                disabled={busy}
                data-testid={`character-save-btn-${character.character_id}`}
                className="text-[10px] font-mono uppercase tracking-wider px-2 py-1 bg-yellow-400 text-black rounded-sm hover:bg-yellow-300 disabled:opacity-50"
              >
                Save
              </button>
              <button
                type="button"
                onClick={() => {
                  setDraft({
                    name: character.name,
                    description: character.description || "",
                    role: character.role || "",
                  });
                  setEditing(false);
                }}
                disabled={busy}
                className="text-[10px] font-mono uppercase tracking-wider px-2 py-1 border border-zinc-700 text-zinc-400 rounded-sm hover:border-zinc-500"
              >
                Cancel
              </button>
            </>
          ) : (
            <>
              <button
                type="button"
                onClick={() => setEditing(true)}
                data-testid={`character-edit-btn-${character.character_id}`}
                className="text-[10px] font-mono uppercase tracking-wider px-2 py-1 border border-zinc-700 text-zinc-400 rounded-sm hover:border-yellow-400 hover:text-yellow-400"
              >
                Edit
              </button>
              <button
                type="button"
                onClick={remove}
                disabled={busy}
                data-testid={`character-delete-btn-${character.character_id}`}
                title={`Delete ${character.name}`}
                className="text-[10px] font-mono uppercase tracking-wider px-2 py-1 border border-zinc-700 text-zinc-400 rounded-sm hover:border-red-500 hover:text-red-400 disabled:opacity-50"
              >
                ✕
              </button>
            </>
          )}
        </div>
      </div>
      {editing ? (
        <textarea
          data-testid={`character-description-input-${character.character_id}`}
          value={draft.description}
          onChange={(e) => setDraft({ ...draft, description: e.target.value })}
          rows={3}
          placeholder="Physical description (e.g. Blonde girl, 5 years old, pink dress, blue eyes, big smile). The more concrete, the more consistent across generated images."
          className="w-full bg-black border border-zinc-700 text-zinc-200 px-2 py-1.5 text-sm rounded-sm font-mono leading-relaxed"
        />
      ) : character.description ? (
        <p className="text-sm text-zinc-300 leading-relaxed whitespace-pre-wrap">
          {character.description}
        </p>
      ) : (
        <p className="text-xs text-zinc-600 italic">No description yet — click Edit to add one.</p>
      )}
      <div>
        <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500 mb-2 flex items-center justify-between">
          <span>Reference photos ({images.length})</span>
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            disabled={busy}
            data-testid={`character-add-image-btn-${character.character_id}`}
            className="text-[10px] font-mono uppercase tracking-wider text-yellow-400 hover:text-yellow-300 disabled:opacity-50"
          >
            + Upload images
          </button>
        </div>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/png,image/jpeg,image/webp,image/gif"
          multiple
          className="hidden"
          onChange={(e) => handleFiles(e.target.files)}
          data-testid={`character-file-input-${character.character_id}`}
        />
        {images.length > 0 ? (
          <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
            {images.map((img) => (
              <CharacterImage
                key={img.image_id}
                characterId={character.character_id}
                image={img}
                onDelete={removeImage}
                disabled={busy}
              />
            ))}
          </div>
        ) : (
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            disabled={busy}
            data-testid={`character-add-image-empty-${character.character_id}`}
            className="block w-full border border-dashed border-zinc-800 rounded-sm py-6 text-center text-xs text-zinc-600 hover:border-yellow-400/50 hover:text-yellow-400 transition-colors disabled:opacity-50"
          >
            Drop or click to upload reference photos (PNG/JPG/WEBP/GIF, ≤8MB each)
          </button>
        )}
      </div>
    </div>
  );
}

function CreateCharacterForm({ onCreated }) {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState({ name: "", description: "", role: "" });
  const [files, setFiles] = useState([]);
  const [busy, setBusy] = useState(false);
  const dropFileRef = useRef(null);

  const addFiles = (incoming) => {
    if (!incoming || !incoming.length) return;
    // Eagerly snapshot the FileList — the onChange handler resets
    // `dropFileRef.current.value` to "" right after calling us, which
    // empties the FileList. If we hold a reference to `incoming` and
    // only iterate it inside the (async) setFiles updater, the snapshot
    // is gone by then and the picker silently drops every selection.
    const picked = Array.from(incoming);
    // Cap at 6 refs per character — anything more rarely helps the
    // image model and bloats the GridFS footprint.
    setFiles((prev) => [...prev, ...picked].slice(0, 6));
  };

  const removeFile = (idx) =>
    setFiles((prev) => prev.filter((_, i) => i !== idx));

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!draft.name.trim()) {
      toast.error("Name is required");
      return;
    }
    setBusy(true);
    try {
      const created = await createCharacter(draft);
      // Upload any picked refs sequentially so partial failures
      // don't leave the character without a parent doc.
      let latest = created;
      for (const f of files) {
        try {
          latest = await uploadCharacterImage(created.character_id, f);
        } catch (err) {
          toast.error(err?.response?.data?.detail || `Couldn't upload ${f.name}`);
        }
      }
      toast.success(
        files.length
          ? `Added ${created.name} with ${files.length} reference photo${files.length === 1 ? "" : "s"}`
          : `Added ${created.name}`,
      );
      onCreated(latest);
      setDraft({ name: "", description: "", role: "" });
      setFiles([]);
      setOpen(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't create character");
    } finally {
      setBusy(false);
    }
  };

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        data-testid="character-create-open-btn"
        className="bg-yellow-400 text-black font-black uppercase tracking-wide px-5 py-3 rounded-sm hover:bg-yellow-300 transition-colors text-sm"
      >
        + Add character
      </button>
    );
  }

  return (
    <form
      onSubmit={submit}
      data-testid="character-create-form"
      className="border border-yellow-400/40 bg-zinc-950/60 rounded-sm p-4 space-y-3"
    >
      <h3 className="font-mono text-[11px] uppercase tracking-[0.25em] text-yellow-400">
        New character
      </h3>
      <input
        data-testid="character-create-name"
        value={draft.name}
        onChange={(e) => setDraft({ ...draft, name: e.target.value })}
        placeholder="Name *  (e.g. Bella)"
        className="w-full bg-black border border-zinc-700 text-white px-3 py-2 rounded-sm focus:border-yellow-400 outline-none"
        autoFocus
        required
      />
      <input
        data-testid="character-create-role"
        value={draft.role}
        onChange={(e) => setDraft({ ...draft, role: e.target.value })}
        placeholder="Role  (e.g. main, sidekick, parent)"
        className="w-full bg-black border border-zinc-700 text-white px-3 py-2 rounded-sm focus:border-yellow-400 outline-none"
      />
      <textarea
        data-testid="character-create-description"
        value={draft.description}
        onChange={(e) => setDraft({ ...draft, description: e.target.value })}
        rows={3}
        placeholder="Physical description (the more concrete, the more consistent across generated images)"
        className="w-full bg-black border border-zinc-700 text-zinc-200 px-3 py-2 rounded-sm focus:border-yellow-400 outline-none text-sm font-mono"
      />
      <div>
        <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500 mb-2">
          Reference photos ({files.length}/6)
          <span className="ml-2 normal-case tracking-normal text-zinc-600">
            — optional, but they massively improve image consistency
          </span>
        </div>
        <input
          ref={dropFileRef}
          type="file"
          accept="image/png,image/jpeg,image/webp,image/gif"
          multiple
          className="hidden"
          onChange={(e) => {
            addFiles(e.target.files);
            // Reset so the same filename can be picked again later.
            if (dropFileRef.current) dropFileRef.current.value = "";
          }}
          data-testid="character-create-file-input"
        />
        {files.length > 0 && (
          <div className="grid grid-cols-3 sm:grid-cols-4 gap-2 mb-2">
            {files.map((f, idx) => (
              <div
                key={idx}
                data-testid={`character-create-preview-${idx}`}
                className="relative group border border-zinc-800 rounded-sm overflow-hidden bg-zinc-950"
              >
                <img
                  src={URL.createObjectURL(f)}
                  alt={f.name}
                  className="w-full aspect-square object-cover"
                />
                <button
                  type="button"
                  onClick={() => removeFile(idx)}
                  data-testid={`character-create-preview-remove-${idx}`}
                  className="absolute top-1 right-1 bg-black/80 hover:bg-red-600 text-white text-[10px] font-bold px-2 py-0.5 rounded-sm opacity-0 group-hover:opacity-100 transition-opacity"
                  title="Remove"
                >
                  ✕
                </button>
              </div>
            ))}
          </div>
        )}
        <button
          type="button"
          onClick={() => dropFileRef.current?.click()}
          data-testid="character-create-add-files-btn"
          className="block w-full border border-dashed border-zinc-700 hover:border-yellow-400/60 hover:text-yellow-400 rounded-sm py-3 text-center text-xs text-zinc-500 transition-colors"
        >
          {files.length ? "+ Add more reference photos" : "+ Add reference photos (PNG/JPG/WEBP/GIF, ≤8MB each)"}
        </button>
      </div>
      <div className="flex gap-2">
        <button
          type="submit"
          disabled={busy}
          data-testid="character-create-submit"
          className="bg-yellow-400 text-black font-black uppercase tracking-wide px-4 py-2 rounded-sm hover:bg-yellow-300 disabled:opacity-50 text-sm"
        >
          {busy ? (files.length ? "Creating & uploading…" : "Creating…") : "Create"}
        </button>
        <button
          type="button"
          onClick={() => {
            setOpen(false);
            setFiles([]);
          }}
          disabled={busy}
          className="font-mono text-xs uppercase tracking-wider px-4 py-2 border border-zinc-700 text-zinc-400 rounded-sm hover:border-zinc-500"
        >
          Cancel
        </button>
      </div>
    </form>
  );
}

export default function Characters() {
  const [characters, setCharacters] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setCharacters(await listCharacters());
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't load characters");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const onCreated = (created) => setCharacters((prev) => [created, ...prev]);
  const onChange = (updated) =>
    setCharacters((prev) => prev.map((c) => (c.character_id === updated.character_id ? updated : c)));
  const onDelete = (id) =>
    setCharacters((prev) => prev.filter((c) => c.character_id !== id));

  return (
    <div
      data-testid="characters-page"
      className="px-6 lg:px-10 py-8 max-w-[1280px] mx-auto"
    >
      <div className="mb-6">
        <div className="font-mono text-[11px] uppercase tracking-[0.3em] text-yellow-400 mb-2">
          phase 2 · launch your channel · character library
        </div>
        <h1 className="text-white text-4xl font-black mb-2">Character library</h1>
        <p className="text-zinc-400 text-sm max-w-2xl">
          Build a reusable cast for your channel. Upload reference photos
          for each character — Bella, Benny, Mom, etc. — so they look
          consistent across the avatar, banner, every thumbnail, and every
          scene. Channels and storyboards can pull from this library on
          demand.
        </p>
      </div>

      <div className="mb-6">
        <CreateCharacterForm onCreated={onCreated} />
      </div>

      {loading ? (
        <div className="font-mono text-[11px] text-zinc-500">
          [recon] loading characters<span className="blink">_</span>
        </div>
      ) : characters.length === 0 ? (
        <div
          data-testid="characters-empty"
          className="border border-dashed border-zinc-800 rounded-sm py-16 text-center text-zinc-500"
        >
          <p className="text-sm">No characters yet.</p>
          <p className="text-xs mt-1">
            Click <strong className="text-yellow-400">+ Add character</strong> above
            to add your first one.
          </p>
        </div>
      ) : (
        <div className="grid lg:grid-cols-2 gap-4">
          {characters.map((c) => (
            <CharacterCard
              key={c.character_id}
              character={c}
              onChange={onChange}
              onDelete={onDelete}
            />
          ))}
        </div>
      )}
    </div>
  );
}
